import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { USER_TYPES } from '../util/userHelpers';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const GroupPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/GroupsPage/GroupPage'));
const GroupsPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/GroupsPage/GroupsPage'));
const HostedSitesPage = loadable(() => import(/* webpackChunkName: "HostedSitesPage" */ '../containers/HostedSitesPage/HostedSitesPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const InviteUsersPage = loadable(() => import(/* webpackChunkName: "InviteUsersPage" */ '../containers/InviteUsersPage/InviteUsersPage'));
const SellerGrowthPage = loadable(() => import(/* webpackChunkName: "SellerGrowthPage" */ '../containers/SellerGrowthPage/SellerGrowthPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const StorePage = loadable(() => import(/* webpackChunkName: "StorePage" */ '../containers/StorePage/StorePage'));
const TeamPage = loadable(() => import(/* webpackChunkName: "TeamPage" */ '../containers/TeamPage/TeamPage'));
const DealersDirectoryPage = loadable(() => import(/* webpackChunkName: "DealersDirectoryPage" */ '../containers/DealersDirectoryPage/DealersDirectoryPage'));
const BookmarkedListingsPage = loadable(() => import(/* webpackChunkName: "BookmarkedListingsPage" */ '../containers/BookmarkedListingsPage/BookmarkedListingsPage'));
const SearchProductsPage = loadable(() => import(/* webpackChunkName: "SearchProductsPage" */ '../containers/SearchProductsPage/SearchProductsPage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
// const BusinessProfileSettingsPage = loadable(() => import(/* webpackChunkName: "BusinessProfileSettingsPage" */ '../containers/BusinessProfileSettingsPage/BusinessProfileSettingsPage'));
const StoreIntegrationPage = loadable(() => import(/* webpackChunkName: "StoreIntegrationPage" */ '../containers/StoreIntegrationPage/StoreIntegrationPage'));
const SellerStatisticsPage = loadable(() => import(/* webpackChunkName: "SellerStatisticsPage" */ '../containers/SellerStatisticsPage/SellerStatisticsPage'));
const SellerWebsiteAdminPage = loadable(() => import(/* webpackChunkName: "SellerWebsiteAdminPage" */ '../containers/SellerWebsiteAdminPage/SellerWebsiteAdminPage'));
const LightspeedIntegrationPage = loadable(() => import(/* webpackChunkName: "LightspeedIntegrationPage" */ '../containers/LightspeedIntegrationPage/LightspeedIntegrationPage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithMapStyled = loadable(() => import(/* webpackChunkName: "SearchPageWithMapStyled" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMapStyled'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const SearchPageWithGridStyled = loadable(() => import(/* webpackChunkName: "SearchPageWithGridStyled" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGridStyled'));
const SearchPageRecentlyViewed = loadable(() => import(/* webpackChunkName: "SearchPageRecentlyViewed" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageRecentlyViewed'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const SellerOnboardingV2Page = loadable(() => import(/* webpackChunkName: "SellerOnboardingV2Page" */ '../containers/SellerOnboardingV2Page/SellerOnboardingV2Page'));
const AffirmReturnPage = loadable(() => import(/* webpackChunkName: "AffirmReturnPage" */ '../containers/AffirmReturnPage/AffirmReturnPage'));
const AdminCommissionPage = loadable(() => import(/* webpackChunkName: "AdminCommissionPage" */ '../containers/AdminCommissionPage/AdminCommissionPage'));
const AdminPanelPage = loadable(() => import(/* webpackChunkName: "AdminPanelPage" */ '../containers/AdminPanelPage/AdminPanelPage'));
const LandingPageCustom = loadable(() => import(/* webpackChunkName: "LandingPageCustom" */ '../containers/LandingPage/LandingPageCustom'));
// Cart components commented out since flow is disabled
// const CartPage = loadable(() => import(/* webpackChunkName: "CartPage" */ '../containers/CartPage/CartPage'));
// const CartCheckoutPage = loadable(() => import(/* webpackChunkName: "CartCheckoutPage" */ '../containers/CartCheckoutPage/CartCheckoutPage'));
// const CartOrderConfirmationPage = loadable(() => import(/* webpackChunkName: "CartOrderConfirmationPage" */ '../containers/CartOrderConfirmationPage/CartOrderConfirmationPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig) => {
  // Use the appropriate SearchPage variant based on layout config
  const SearchPage = layoutConfig.searchPage?.variantType === 'map'
    ? SearchPageWithMap
    : SearchPageWithGrid;
  const RecentlyViewedPage = SearchPageRecentlyViewed;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel'
    ? ListingPageCarousel
    : ListingPageCoverPhoto;

  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPageCustom,
    },
    {
      path: '/s',
      name: 'SearchPage',
      component: LandingPageCustom,
    },
    {
      path: '/staff-picks',
      name: 'StaffPicksPage',
      component: LandingPageCustom,
    },
    {
      path: '/services',
      name: 'ServicesSearchPage',
      component: LandingPageCustom,
    },
    {
      path: '/search',
      name: 'SearchPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/category/:category',
      name: 'CategoryPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/become-a-seller',
      name: 'BecomeASellerPage',
      component: SellerGrowthPage,
    },
    {
      path: '/hosted-sites',
      name: 'HostedSitesPage',
      component: HostedSitesPage,
    },
    {
      path: '/recently-viewed',
      name: 'RecentlyViewedPage',
      auth: true,
      component: RecentlyViewedPage,
      loadData: pageDataLoadingAPI.RecentlyViewedPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/store/:id',
      name: 'StorePage',
      component: StorePage,
      loadData: pageDataLoadingAPI.StorePage.loadData,
    },
    {
      path: '/find-a-dealer',
      name: 'FindADealerPage',
      component: DealersDirectoryPage,
      loadData: pageDataLoadingAPI.DealersDirectoryPage.loadData,
    },
    {
      path: '/dealers',
      name: 'DealersDirectoryPage',
      component: DealersDirectoryPage,
      loadData: pageDataLoadingAPI.DealersDirectoryPage.loadData,
    },
    {
      path: '/bookmarks',
      name: 'BookmarkedListingsPage',
      auth: true,
      component: BookmarkedListingsPage,
      loadData: pageDataLoadingAPI.BookmarkedListingsPage.loadData,
    },
    {
      path: '/cart',
      name: 'CartPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/cart/checkout',
      name: 'CartCheckoutPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/cart/order-confirmed',
      name: 'CartOrderConfirmationPage',
      auth: true,
      authPage: 'LoginPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/cart/epro-checkout',
      name: 'EproCheckoutPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/search-products',
      name: 'SearchProductsPage',
      component: SearchProductsPage,
    },
    {
      path: '/store/:id/member/:memberId',
      name: 'TeamPageMember',
      component: TeamPage,
      loadData: pageDataLoadingAPI.TeamPage.loadData,
    },
    {
      path: '/store/:id/:type',
      name: 'TeamPage',
      component: TeamPage,
      loadData: pageDataLoadingAPI.TeamPage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    /* {
      path: '/business-profile-settings',
      name: 'BusinessProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: BusinessProfileSettingsPage,
    }, */
    {
      path: '/store-integration',
      name: 'StoreIntegrationPage',
      auth: true,
      authPage: 'LoginPage',
      component: StoreIntegrationPage,
    },
    {
      path: '/statistics',
      name: 'SellerStatisticsPage',
      auth: true,
      authPage: 'LoginPage',
      component: SellerStatisticsPage,
    },
    {
      path: '/website-admin',
      name: 'SellerWebsiteAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: SellerWebsiteAdminPage,
    },
    {
      path: '/lightspeed-integration',
      name: 'LightspeedIntegrationPage',
      auth: true,
      authPage: 'LoginPage',
      component: LightspeedIntegrationPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
      // component: () => <NamedRedirect name="SignupForUserTypePage" params={{ userType: USER_TYPES.BUYER }} />,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/orderconfirmed/:id',
      name: 'OrderConfirmedPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/guest/orderconfirmed/:id',
      name: 'GuestOrderConfirmedPage',
      auth: false, // No authentication required for guests
      component: loadable(() => import(/* webpackChunkName: "GuestOrderConfirmedPage" */ '../containers/GuestOrderConfirmedPage/GuestOrderConfirmedPage')),
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/team',
      name: 'InviteUsersPage',
      auth: true,
      authPage: 'LoginPage',
      component: InviteUsersPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account/teams/',
      name: 'GroupsPage',
      component: GroupsPage,
      loadData: pageDataLoadingAPI.GroupsPage.loadData,
    },
    {
      path: '/account/teams/:id',
      name: 'GroupPage',
      component: GroupPage,
      loadData: pageDataLoadingAPI.GroupPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
    {
      path: '/onboarding',
      name: 'OnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: SellerOnboardingV2Page,
      loadData: pageDataLoadingAPI.SellerOnboardingV2Page.loadData,
    },
    {
      path: '/onboarding/stripe/:returnURLType',
      name: 'SellerOnboardingV2PageStripeReturn',
      auth: true,
      authPage: 'LoginPage',
      component: SellerOnboardingV2Page,
      loadData: pageDataLoadingAPI.SellerOnboardingV2Page.loadData,
    },
    {
      path: '/checkout-affirm-return',
      name: 'AffirmReturnPage',
      auth: true,
      authPage: 'LoginPage',
      component: AffirmReturnPage,
    },
    {
      path: '/admin/commission-management',
      name: 'AdminCommissionPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminCommissionPage,
    },
    {
      path: '/admin/panel',
      name: 'AdminPanelPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminPanelPage,
    }
  ];
};

export default routeConfiguration;
